import { Injectable } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  AbstractControl,
  Validators,
  FormArray,
  FormControl,
} from '@angular/forms';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import {
  ISimulationForm,
  ISimulationDetail,
  ISimulationDetailsForm,
  ISectionWeightForm,
  IKnowledgeCheckAnswerPoint,
  IKnowledgeCheckQuestionForm,
} from 'src/app/interfaces/self-serve/isimulation-details-information';
import { ISimulationPrompt } from 'src/app/interfaces/self-serve/isimulation-prompt';
import {
  IScoreWeight,
  CalcType,
  WEIGHT_KEY_TO_SECTION,
  SECTION_TO_WEIGHT_KEY,
  SECTION_ORDER,
  SECTION_ORDER_DEFAULT,
  SectionType,
} from 'src/app/interfaces/self-serve/isimulation-score';
import { ISectionsWithFacts } from 'src/app/interfaces/self-serve/responses/ISectionsWithFacts';
import { IFetchSimulationDetails } from 'src/app/interfaces/self-serve/responses/ifetch-simulation-details';
import { IGetWordDetailsResponse } from 'src/app/interfaces/self-serve/responses/iget-word-details-response';
import { IWordDefinition } from 'src/app/interfaces/self-serve/responses/iword-definition';
import { IKeywordsFormGroup, KnowledgeCheckAnswerPointVerb } from 'src/app/interfaces/self-serve/isimulation-details-information';
import { SimulationDetailsDefaultTitles } from 'src/app/utils/constants/self-serve/simulation-details.constants';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { environment } from 'src/environments/environment';
import { PairedFieldItem, WordItem } from '@qai/ui';
import { SelfAuthoringPersonaFormService } from './self-authoring-persona-form.service';

export interface SimulationFormOptions {
  skipConversePromptValidation?: boolean;
  /** Knowledge Check (GUIDED) has no persona, prompts or scoring, so their validators are dropped. */
  isKnowledgeCheck?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthorReactiveFormService {
  backendUrl: string = environment.backendUrl;

  private simulations = new Map<string, BehaviorSubject<FormGroup>>();
  private updatedAtMap = new Map<string, BehaviorSubject<string>>();
  private simulationStatuses = new Map<string, string>();
  private readonly readonlyStatuses = new Set(['PUBLISHED']);
  private isSelfAuthoringActive = false;
  private replicaIdSyncSubscriptions = new Map<string, Subscription>();

  constructor(
    private fb: FormBuilder,
    private selfAuthoringPersonaFormService: SelfAuthoringPersonaFormService
  ) {}

  setSelfAuthoring(value: boolean): void {
    this.isSelfAuthoringActive = value;
  }

  get isSelfAuthoring(): boolean {
    return this.isSelfAuthoringActive;
  }

  setSimulationStatus(uuid: string, status: string): void {
    this.simulationStatuses.set(uuid, status);
  }

  getSimulationStatus(uuid: string): string | undefined {
    return this.simulationStatuses.get(uuid);
  }

  isReadOnly(uuid: string): boolean {
    const status = this.simulationStatuses.get(uuid);
    return this.readonlyStatuses.has(status ?? '');
  }

  setReadOnlyMode(uuid: string, field: AbstractControl | null | undefined) {
    if (!field) return;
    const readOnly = this.isReadOnly(uuid);
    if (readOnly && !field.disabled) {
      field.disable({ emitEvent: false });
    } else if (!readOnly && field.disabled) {
      field.enable({ emitEvent: false });
    }
  }

  createSimulation(initialData = {}, uuid: string): string {
    const form = this.createSimulationForm(initialData);
    this.simulations.set(uuid, new BehaviorSubject(form));

    // Save initial state to localStorage
    this.saveToLocalStorage(uuid, form);

    return uuid;
  }

  initializeSimulationForm(
    uuid: string,
    apiData: IFetchSimulationDetails,
    keywordsData?: IGetWordDetailsResponse,
    options: SimulationFormOptions = {}
  ): FormGroup {
    const transformed = this.transformApiData(apiData, keywordsData);
    const form = this.createSimulationForm(transformed, options);
    this.setSimulationStatus(uuid, apiData.status);

    // Set the updatedAt value in the updatedAtMap
    if (transformed.updatedAt) {
      if (!this.updatedAtMap.has(uuid)) {
        this.updatedAtMap.set(
          uuid,
          new BehaviorSubject<string>(transformed.updatedAt)
        );
      } else {
        this.updatedAtMap.get(uuid)!.next(transformed.updatedAt);
      }
    }

    if (this.simulations.has(uuid)) {
      this.simulations.get(uuid)!.next(form);
    } else {
      this.simulations.set(uuid, new BehaviorSubject<FormGroup>(form));
    }

    this.syncSelfAuthoringReplicaId(uuid, form);

    return form;
  }

  private syncSelfAuthoringReplicaId(uuid: string, form: FormGroup): void {
    this.replicaIdSyncSubscriptions.get(uuid)?.unsubscribe();

    if (!this.isSelfAuthoringActive) {
      return;
    }

    const replicaIdControl = form.get(
      `${SimulationConstants.CALL_PERSONA_PATH}.replicaId`
    );
    const personaNameControl = form.get(
      `${SimulationConstants.CALL_PERSONA_PATH}.personaName`
    );
    const welcomeMessageArray = form.get(
      `${SimulationConstants.CALL_PERSONA_PATH}.welcomeMessage`
    ) as FormArray | null;
    const personaForm = this.selfAuthoringPersonaFormService.getOrCreateForm(uuid);
    const personaReplicaIdControl = personaForm.get('replicaId');

    const subscription = personaReplicaIdControl?.valueChanges
      .pipe(startWith(personaReplicaIdControl.value))
      .subscribe((replicaId: string) => {
        if (replicaId) {
          replicaIdControl?.patchValue(replicaId, { emitEvent: false });
          personaNameControl?.patchValue(
            personaForm.get('personaName')?.value,
            { emitEvent: false }
          );

          const personaWelcomeMessages = (
            personaForm.get('welcomeMessage') as FormArray
          )?.value as string[];
          welcomeMessageArray?.controls.forEach((control, index) => {
            if (personaWelcomeMessages?.[index] !== undefined) {
              control.patchValue(personaWelcomeMessages[index], {
                emitEvent: false,
              });
            }
          });
        }
      });

    if (subscription) {
      this.replicaIdSyncSubscriptions.set(uuid, subscription);
    }
  }

  getForm(uuid: string): Observable<FormGroup> {
    if (!this.simulations.has(uuid)) {
      throw new Error(`Form with UUID ${uuid} has not been initialized yet.`);
    }
    return this.simulations.get(uuid)!.asObservable();
  }

  patchForm(uuid: string, section: string, data: any): void {
    const subject = this.simulations.get(uuid);
    if (!subject) return;

    const currentForm = subject.value;
    console.log('current form:', currentForm);

    const sectionGroup = currentForm.get(section) as FormGroup;

    if (sectionGroup) {
      sectionGroup.patchValue(data, { onlySelf: true, emitEvent: true });
      // subject.next(currentForm);   // rebind the form to all value changes listeners

      // Save updated form to localStorage
      this.saveToLocalStorage(uuid, currentForm);
    }
  }

  /** Same as patchForm but never emits valueChanges — for programmatic syncs
   * (e.g. a background refetch) that must not be mistaken for a user edit by
   * an autosave subscription listening on the same section. */
  patchFormSilently(uuid: string, section: string, data: any): void {
    const subject = this.simulations.get(uuid);
    if (!subject) return;

    const currentForm = subject.value;
    const sectionGroup = currentForm.get(section) as FormGroup;

    if (sectionGroup) {
      sectionGroup.patchValue(data, { onlySelf: true, emitEvent: false });
      this.saveToLocalStorage(uuid, currentForm);
    }
  }

  /** to save data array of FormArray of FormControl<string> */
  patchFormArrayValues(
    array: FormArray,
    values: string[],
    applyValidators: boolean,
    emitEvent: false
  ): void {
    array.clear({ emitEvent: false });

    values.forEach((val) => {
      const control = applyValidators
        ? this.fb.control(val, Validators.required)
        : this.fb.control(val);

      array.push(control, { emitEvent });
    });
  }

  deleteSimulation(uuid: string): void {
    this.simulations.delete(uuid);
    this.replicaIdSyncSubscriptions.get(uuid)?.unsubscribe();
    this.replicaIdSyncSubscriptions.delete(uuid);
    localStorage.removeItem(`simulation-${uuid}`);
  }

  getUpdatedAt$(uuid: string): Observable<string> {
    if (!this.updatedAtMap.has(uuid)) {
      const stored = this.loadFromLocalStorage(uuid);

      // Try to get updatedAt from stored data, form, or use current timestamp
      let timestamp = stored?.updatedAt;

      if (!timestamp && this.simulations.has(uuid)) {
        const form = this.simulations.get(uuid)!.value;
        timestamp = form.get('updatedAt')?.value;
      }

      timestamp = timestamp ?? new Date().toISOString();
      this.updatedAtMap.set(uuid, new BehaviorSubject<string>(timestamp));
    }

    return this.updatedAtMap.get(uuid)!.asObservable();
  }

  updateFormUpdatedAt(uuid: string): void {
    const subject = this.simulations.get(uuid);
    if (!subject) return;

    const currentForm = subject.value;
    const updatedAt = new Date().toISOString();

    // Update the form's updatedAt field
    currentForm.patchValue({ updatedAt }, { emitEvent: false });

    // Save to localStorage
    this.saveToLocalStorage(uuid, currentForm);
  }

  private createSimulationForm(
    data: Partial<ISimulationForm> = {},
    options: SimulationFormOptions = {}
  ): FormGroup {
    const callDetails =
      data.general?.simulationDetailsInfo?.simulationDetailtabs || [];

    const { simulationDetailtabs } =
      this.normalizeCallDetailsToForm(callDetails);

    const buildPromptGroup = (
      prompt: any,
      type: string,
      isRequired: boolean = true
    ): FormGroup =>
      this.fb.group({
        promptType: [type, isRequired ? Validators.required : null],
        promptText: [
          prompt?.promptText || '',
          isRequired ? Validators.required : null,
        ],
        weightPercentage: [
          prompt?.weightPercentage || 0,
          isRequired ? Validators.required : null,
        ],
        uuid: [prompt?.uuid || '', isRequired ? Validators.required : null],
      });

    const form = this.fb.group({
      updatedAt: [data.updatedAt || new Date().toISOString()],
      general: this.fb.group({
        simulationName: [
          data.general?.simulationName || '',
          Validators.required,
        ],
        simulationInfo: [data.general?.simulationInfo || ''],
        simulationDescription: [data.general?.simulationDescription || ''],
        moduleUuid: [data.general?.moduleUuid || ''],
        moduleName: [data.general?.moduleName || ''],
        language: [data.general?.language || 'en'],
        callPersona: this.fb.group({
          uuid: [data.general?.callPersona?.uuid || ''],
          personaId: data.general?.callPersona?.personaId || '',
          replicaId: [
            data.general?.callPersona?.replicaId || '',
            Validators.required,
          ],
          personaName: [
            data.general?.callPersona?.personaName || '',
            Validators.required,
          ],
          welcomeMessage: this.fb.array(
            (data.general?.callPersona?.welcomeMessage || ['']).map((msg) =>
              this.fb.control(msg, Validators.required)
            )
          ),
          resumeMessage: this.fb.array(
            (data.general?.callPersona?.resumeMessage || ['']).map((msg) =>
              this.fb.control(msg)
            )
          ),
          customBackgroundImage: [
            data.general?.callPersona?.customBackgroundImage || null,
          ],
          customBackgroundUrl: [
            data.general?.callPersona?.customBackgroundUrl || '',
          ],
          customThumbnailUrl: [
            data.general?.callPersona?.customThumbnailUrl || '',
          ],
          avatarUrl: [data.general?.callPersona?.avatarUrl || null],
        }),
        simulationDetailsInfo: this.fb.group({
          simulationDetailtabs: this.fb.array(
            simulationDetailtabs.map((tab) =>
              this.fb.group({
                tabTitle: [tab.tabTitle],
                tabText: [tab.tabText],
                uuid: [tab.uuid],
              })
            )
          ),
          additionalSectionTitle: [
            data.general?.simulationDetailsInfo?.additionalSectionTitle || '',
          ],
          additionalSectionInfo: [
            data.general?.simulationDetailsInfo?.additionalSectionInfo || '',
          ],
          additionalSectionUuid: [
            data.general?.simulationDetailsInfo?.additionalSectionUuid || null,
          ],
          callDetailsAuthorOverride: [
            data.general?.simulationDetailsInfo?.callDetailsAuthorOverride ?? false,
          ],
        }),
        screenshareEnabled: [data.general?.screenshareEnabled ?? false],
      }),
      content: this.fb.group({
        conversePrompt: buildPromptGroup(
          data.content?.conversePrompt,
          SimulationConstants.CONVERSE_PROMPT_TYPE,
          !options.skipConversePromptValidation
        ),
        cleanTranscriptPrompt: buildPromptGroup(
          data.content?.cleanTranscriptPrompt,
          SimulationConstants.CLEAN_TRANSCRIPT_PROMPT_TYPE,
          false
        ),
        sttPrompt: buildPromptGroup(
          data.content?.sttPrompt,
          SimulationConstants.STT_PROMPT_TYPE,
          false
        ),
      }),
      scoring: this.fb.group({
        overallScorePrompt: buildPromptGroup(
          data.scoring?.overallScorePrompt,
          SimulationConstants.OVERALL_SCORE_PROMPT_TYPE,
          !this.isSelfAuthoringActive
        ),
        messagingPrompt: buildPromptGroup(
          data.scoring?.messagingPrompt,
          data.scoring?.messagingPrompt?.promptType ||
            SimulationConstants.MESSAGING_PROMPT_TYPE
        ),
        messagingTheme: this.buildMessagingGroupMessagingTheme(
          data.scoring?.messagingThemes
        ),
        methodologyPrompt: this.withRubricControl(
          buildPromptGroup(
            data.scoring?.methodologyPrompt,
            SimulationConstants.METHODOLOGY_PROMPT_TYPE
          ),
          data.scoring?.methodologyPrompt?.rubric
        ),
        skillPrompt: this.withRubricControl(
          buildPromptGroup(
            data.scoring?.skillPrompt,
            SimulationConstants.SKILL_PROMPT_TYPE
          ),
          data.scoring?.skillPrompt?.rubric
        ),
        sectionWeights: [data.scoring?.sectionWeights || []],
        accuracyFeedback: this.fb.group({
          enabled: [data.scoring?.accuracyFeedback?.enabled ?? false],
          documentCount: [data.scoring?.accuracyFeedback?.documentCount ?? 0],
        }),
      }),
      keywords: this.fb.group({
        stt_words: this.fb.array(
          (data.keywords?.stt_words || []).map((word) =>
            this.fb.group({
              key: [word.key || ''],
              value: [word.value || null],
              scope: [word.scope || ''],
              typeName: [word.typeName || ''],
              definitionUuid: [word.definitionUuid || ''],
              wordTypeUuid: [word.wordTypeUuid || ''],
            })
          )
        ),
        call_words: this.fb.array(
          (() => {
            const callWordsData = data.keywords?.call_words || [];
            return callWordsData.map((word) =>
            this.fb.group({
              key: [word.key || ''],
              value: [word.value || null],
              scope: [word.scope || ''],
              typeName: [word.typeName || ''],
              definitionUuid: [word.definitionUuid || ''],
                wordTypeUuid: [word.wordTypeUuid || ''],
            })
            );
          })()
        ),
        replace_words: this.fb.array(
          (data.keywords?.replace_words || []).map((word) =>
            this.fb.group({
              key: [word.key || ''],
              value: [word.value || null],
              scope: [word.scope || ''],
              typeName: [word.typeName || ''],
              definitionUuid: [word.definitionUuid || ''],
              wordTypeUuid: [word.wordTypeUuid || ''],
            })
          )
        ),
        pronounce_words: this.fb.array(
          (data.keywords?.pronounce_words || []).map((wordDef) =>
            this.fb.group({
              key: [wordDef.key || ''],
              value: [wordDef.value || null],
              scope: [wordDef.scope || ''],
              typeName: [wordDef.typeName || ''],
              definitionUuid: [wordDef.definitionUuid || ''],
              wordTypeUuid: [wordDef.wordTypeUuid || ''],
            })
          )
        ),
        clean_transcript_words: this.fb.array(
          (data.keywords?.clean_transcript_words || []).map((word) =>
            this.fb.group({
              key: [word.key || ''],
              value: [word.value || null],
              scope: [word.scope || ''],
              typeName: [word.typeName || ''],
              definitionUuid: [word.definitionUuid || ''],
              wordTypeUuid: [word.wordTypeUuid || ''],
            })
          )
        ),
        // Store wordTypeUuid for each word type even when there are no words
        stt_words_wordTypeUuid: [data.keywords?.stt_words_wordTypeUuid || ''],
        call_words_wordTypeUuid: [data.keywords?.call_words_wordTypeUuid || ''],
        replace_words_wordTypeUuid: [
          data.keywords?.replace_words_wordTypeUuid || '',
        ],
        pronounce_words_wordTypeUuid: [
          data.keywords?.pronounce_words_wordTypeUuid || '',
        ],
        clean_transcript_words_wordTypeUuid: [
          data.keywords?.clean_transcript_words_wordTypeUuid || '',
        ],
        // Store hasProductScopedWords for each word type
        stt_words_hasProductScopedWords: [
          data.keywords?.stt_words_hasProductScopedWords ?? false,
        ],
        call_words_hasProductScopedWords: [
          data.keywords?.call_words_hasProductScopedWords ?? false,
        ],
        replace_words_hasProductScopedWords: [
          data.keywords?.replace_words_hasProductScopedWords ?? false,
        ],
        pronounce_words_hasProductScopedWords: [
          data.keywords?.pronounce_words_hasProductScopedWords ?? false,
        ],
        clean_transcript_words_hasProductScopedWords: [
          data.keywords?.clean_transcript_words_hasProductScopedWords ?? false,
        ],
      }),
      testing: this.fb.group({}),
    });

    const scoringGroup = form.get('scoring') as FormGroup;
    const sectionWeights: ISectionWeightForm[] =
      data.scoring?.sectionWeights || [];

    this.applyScoringValidators(scoringGroup, sectionWeights);

    if (options.isKnowledgeCheck) {
      this.clearValidatorsDeep(form.get(SimulationConstants.CALL_PERSONA_PATH));
      this.clearValidatorsDeep(form.get(SimulationConstants.CONTENT_SECTION));
      this.clearValidatorsDeep(form.get(SimulationConstants.SCORING_SECTION));
      // `form` is typed from its literal shape, so widen it to add the KC-only control.
      (form as FormGroup).addControl(
        SimulationConstants.QUESTIONS_SECTION,
        this.fb.array((data.questions || []).map((question) => this.buildQuestionGroup(question))),
      );
    }

    return form;
  }

  /** Builds one Knowledge Check question group, so new, saved and generated questions share one shape. */
  buildQuestionGroup(question: Partial<IKnowledgeCheckQuestionForm> = {}): FormGroup {
    return this.fb.group({
      questionUuid: [question.questionUuid || ''],
      callGuidedUuid: [question.callGuidedUuid || ''],
      title: [question.title || ''],
      script: [question.script || ''],
      hint: [question.hint || ''],
      resourceNote: [question.resourceNote || ''],
      sequence: [question.sequence ?? 0],
      answerPoints: this.fb.array(
        (question.answerPoints || []).map((ap) => this.buildAnswerPointGroup(ap)),
      ),
    });
  }

  /** Builds one answer point FormGroup for a Knowledge Check question. */
  buildAnswerPointGroup(answerPoint: Partial<IKnowledgeCheckAnswerPoint> = {}): FormGroup {
    return this.fb.group({
      uuid: [answerPoint.uuid || ''],
      verb: [answerPoint.verb || KnowledgeCheckAnswerPointVerb.State],
      displayText: [answerPoint.displayText || ''],
      displayFactIncorrect: [answerPoint.displayFactIncorrect ?? ''],
      sequence: [answerPoint.sequence ?? 0],
    });
  }

  private clearValidatorsDeep(control: AbstractControl | null): void {
    if (!control) return;

    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.values(control.controls).forEach((child) => this.clearValidatorsDeep(child));
    }

    control.clearValidators();
    control.updateValueAndValidity({ emitEvent: false });
  }

  applyScoringValidators(
    scoringGroup: FormGroup,
    sectionWeights: ISectionWeightForm[]
  ) {
    const isEnabled = (section: SectionType) =>
      !sectionWeights.find((s) => s.section === section)?.disabled;

    const setPromptValidation = (
      controlName: string,
      section: SectionType,
      hasRubric: boolean = false
    ) => {
      const group = scoringGroup.get(controlName) as FormGroup;
      if (!group) return;

      if (isEnabled(section)) {
        group.enable({ emitEvent: false });

        const promptTextControl = group.get('promptText');
        const uuidControl = group.get('uuid');
        const rubricControl = group.get('rubric');

        if (this.isSelfAuthoringActive) {
          promptTextControl?.clearValidators();
          uuidControl?.clearValidators();
          rubricControl?.setValidators(hasRubric ? Validators.required : null);
        } else {
          promptTextControl?.setValidators(Validators.required);
          uuidControl?.setValidators(Validators.required);
          rubricControl?.clearValidators();
        }
        group.get('weightPercentage')?.setValidators(Validators.required);

        promptTextControl?.updateValueAndValidity({ onlySelf: true });
        uuidControl?.updateValueAndValidity({ onlySelf: true });
        rubricControl?.updateValueAndValidity({ onlySelf: true });
      } else {
        // Clear validators & disable the entire group
        group.disable({ emitEvent: false });
      }

      group.updateValueAndValidity({ onlySelf: true });
    };

    setPromptValidation(
      SimulationConstants.MESSAGING_PROMPT_SECTION,
      'Messaging'
    );
    setPromptValidation(
      SimulationConstants.METHODOLOGY_PROMPT_SECTION,
      'Methodology',
      true
    );
    setPromptValidation(
      SimulationConstants.SKILL_PROMPT_SECTION,
      'Skills'
    );
  }

  private withRubricControl(group: FormGroup, rubric?: string): FormGroup {
    group.addControl('rubric', this.fb.control(rubric || ''));
    return group;
  }

  public buildMessagingGroupMessagingTheme(
    value?: ISectionsWithFacts
  ): FormGroup {
    const fallbackSection = this.fb.group({
      uuid: [''],
      name: [''],
      facts: this.fb.array([
        this.fb.group({
          uuid: [''],
          rawText: [''],
          displayTextCorrect: [''],
          displayTextIncorrect: [''],
          threshold: [0],
        }),
      ]),
    });

    let sections: FormGroup[];

    if (value && value.factSections && value.factSections.length > 0) {
      sections = value.factSections.map((section) =>
        this.fb.group({
          uuid: [section.uuid || ''],
          name: [section.name || ''],
          facts: this.fb.array(
            (section.facts || []).map((fact) =>
              this.fb.group({
                uuid: [fact.uuid || ''],
                rawText: [fact.rawText || ''],
                displayTextCorrect: [fact.displayTextCorrect || ''],
                displayTextIncorrect: [fact.displayTextIncorrect || ''],
                threshold: [fact.threshold ?? 0],
              })
            )
          ),
        })
      );
    } else {
      sections = [fallbackSection];
    }

    return this.fb.group({
      factSections: this.fb.array(sections),
    });
  }

  saveToLocalStorage(uuid: string, form: FormGroup): void {
    try {
      const updatedAt = new Date().toISOString();

      // Update the form's updatedAt field
      form.patchValue({ updatedAt }, { emitEvent: false });

      const raw = form.getRawValue();
      const dataToSave = {
        updatedAt,
        ...raw,
      };
      localStorage.setItem(`simulation-${uuid}`, JSON.stringify(dataToSave));

      // emit new updatedAt
      if (!this.updatedAtMap.has(uuid)) {
        this.updatedAtMap.set(uuid, new BehaviorSubject<string>(updatedAt));
      } else {
        this.updatedAtMap.get(uuid)!.next(updatedAt);
      }
    } catch (err) {
      console.error('Failed to save form to localStorage:', err);
    }
  }

  private loadFromLocalStorage(uuid: string): any | null {
    try {
      const raw = localStorage.getItem(`simulation-${uuid}`);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.error('Failed to load form from localStorage:', err);
      return null;
    }
  }

  /**
   * Transforms keywords data from API response format to form format
   */
  private transformKeywordsData(
    keywordsData?: IGetWordDetailsResponse
  ): IKeywordsFormGroup | undefined {
    if (!keywordsData || !Array.isArray(keywordsData)) {
      return undefined;
    }

    // Initialize arrays for each word type
    const sttWords: IWordDefinition[] = [];
    const callWords: IWordDefinition[] = [];
    const replaceWords: IWordDefinition[] = [];
    const pronounceWords: IWordDefinition[] = [];
    const cleanTranscriptWords: IWordDefinition[] = [];
    const replaceWordsByValue = new Map<
      string,
      {
        keys: string[];
        words: any[];
        wordTypeUuid: string;
        scope: string;
      }
    >();
    const pronounceWordsByValue = new Map<
      string,
      {
        keys: string[];
        words: any[];
        wordTypeUuid: string;
        scope: string;
      }
    >();

    // Store wordTypeUuid for each word type (even when words array is empty)
    const wordTypeUuidMap = new Map<string, string>();
    // Store hasProductScopedWords for each word type
    const hasProductScopedWordsMap = new Map<string, boolean>();

    // Transform the array-based response structure
    keywordsData.forEach((wordTypeGroup) => {
      const { wordTypeUuid, wordTypeName, words, hasProductScopedWords } = wordTypeGroup;

      // Store wordTypeUuid even if words array is empty
      if (wordTypeUuid) {
        wordTypeUuidMap.set(wordTypeName, wordTypeUuid);
      }
      // Store hasProductScopedWords even if words array is empty
      if (hasProductScopedWords !== undefined) {
        hasProductScopedWordsMap.set(wordTypeName, hasProductScopedWords);
      }

      words.forEach((word) => {
        // Transform API word structure to form word structure
        const transformedWord: IWordDefinition = {
          key: word.wordKey,
          value: word.wordValue,
          scope: word.scope,
          typeName: wordTypeName,
          definitionUuid: word.uuid,
          wordTypeUuid: wordTypeUuid,
        };

        // Group words by type
        switch (wordTypeName) {
          case 'stt_words':
            sttWords.push(transformedWord);
            break;
          case 'call_words':
            callWords.push(transformedWord);
            break;
          case 'replace_words':
            // Group replace words by wordValue (the "to" value)
            const valueKey = word.wordValue || '';
            if (!replaceWordsByValue.has(valueKey)) {
              replaceWordsByValue.set(valueKey, {
                keys: [],
                words: [],
                wordTypeUuid: wordTypeUuid,
                scope: word.scope,
              });
            }
            const group = replaceWordsByValue.get(valueKey)!;
            if (word.wordKey) {
              group.keys.push(word.wordKey);
            }
            group.words.push(word);
            break;
          case 'pronounce_words':
            // Group pronounce words by wordValue (the "to" value)
            const pronounceValueKey = word.wordValue || '';
            if (!pronounceWordsByValue.has(pronounceValueKey)) {
              pronounceWordsByValue.set(pronounceValueKey, {
                keys: [],
                words: [],
                wordTypeUuid: wordTypeUuid,
                scope: word.scope,
              });
            }
            const pronounceGroup =
              pronounceWordsByValue.get(pronounceValueKey)!;
            if (word.wordKey) {
              pronounceGroup.keys.push(word.wordKey);
            }
            pronounceGroup.words.push(word);
            break;
          case 'clean_transcript_words':
            cleanTranscriptWords.push(transformedWord);
            break;
        }
      });
    });

    replaceWordsByValue.forEach((grouped, value) => {
      grouped.words.forEach((word) => {
        replaceWords.push({
          key: word.wordKey,
          value: value,
          scope: grouped.scope,
          typeName: 'replace_words',
          definitionUuid: word.uuid || '',
          wordTypeUuid: grouped.wordTypeUuid,
        });
      });
    });

    pronounceWordsByValue.forEach((grouped, value) => {
      grouped.words.forEach((word) => {
        pronounceWords.push({
          key: word.wordKey,
          value: value,
          scope: grouped.scope,
          typeName: 'pronounce_words',
          definitionUuid: word.uuid || '',
          wordTypeUuid: grouped.wordTypeUuid,
        });
      });
    });

    const result: IKeywordsFormGroup = {
      stt_words: sttWords,
      call_words: callWords,
      replace_words: replaceWords,
      pronounce_words: pronounceWords.length > 0 ? pronounceWords : undefined,
      clean_transcript_words:
        cleanTranscriptWords.length > 0 ? cleanTranscriptWords : undefined,
    };

    // Store wordTypeUuid for each word type even when there are no words
    if (wordTypeUuidMap.has('stt_words')) {
      result.stt_words_wordTypeUuid = wordTypeUuidMap.get('stt_words');
    }
    if (wordTypeUuidMap.has('call_words')) {
      result.call_words_wordTypeUuid = wordTypeUuidMap.get('call_words');
    }
    if (wordTypeUuidMap.has('replace_words')) {
      result.replace_words_wordTypeUuid = wordTypeUuidMap.get('replace_words');
    }
    if (wordTypeUuidMap.has('pronounce_words')) {
      result.pronounce_words_wordTypeUuid =
        wordTypeUuidMap.get('pronounce_words');
    }
    if (wordTypeUuidMap.has('clean_transcript_words')) {
      result.clean_transcript_words_wordTypeUuid = wordTypeUuidMap.get(
        'clean_transcript_words'
      );
    }

    // Store hasProductScopedWords for each word type
    if (hasProductScopedWordsMap.has('stt_words')) {
      result.stt_words_hasProductScopedWords = hasProductScopedWordsMap.get('stt_words');
    }
    if (hasProductScopedWordsMap.has('call_words')) {
      result.call_words_hasProductScopedWords = hasProductScopedWordsMap.get('call_words');
    }
    if (hasProductScopedWordsMap.has('replace_words')) {
      result.replace_words_hasProductScopedWords = hasProductScopedWordsMap.get('replace_words');
    }
    if (hasProductScopedWordsMap.has('pronounce_words')) {
      result.pronounce_words_hasProductScopedWords = hasProductScopedWordsMap.get('pronounce_words');
    }
    if (hasProductScopedWordsMap.has('clean_transcript_words')) {
      result.clean_transcript_words_hasProductScopedWords = hasProductScopedWordsMap.get('clean_transcript_words');
    }

    return result;
  }

  transformApiData(
    apiData: IFetchSimulationDetails,
    keywordsData?: IGetWordDetailsResponse
  ): ISimulationForm {
    const callDetails = apiData.callDetailsList?.callDetails || [];

    const {
      simulationDetailtabs,
      additionalSectionTitle,
      additionalSectionInfo,
      additionalSectionUuid,
    } = this.normalizeCallDetailsToForm(callDetails);

    const prompts = apiData.callPrompts?.prompts || [];
    const transformedKeywords = this.transformKeywordsData(keywordsData);

    return {
      updatedAt: apiData.updatedAt || new Date().toISOString(),
      general: {
        simulationName: apiData.name || '',
        simulationInfo: '',
        simulationDescription: apiData.description || '',
        moduleUuid: apiData.moduleUuid || '',
        moduleName: apiData.moduleName || '',
        language: apiData.language || 'en',
        callPersona: {
          uuid: apiData.callPersona?.uuid || '',
          personaId: apiData.callPersona?.personaId || '',
          replicaId: apiData.callPersona?.replicaId || '',
          personaName: apiData.callPersona?.personaName || '',
          welcomeMessage: apiData.callPersona?.welcomeMessage || [''],
          resumeMessage: apiData.callPersona?.resumeMessage || [''],
          customBackgroundImage:
            apiData.callPersona?.customBackgroundImage || null,
          avatarUrl: apiData.avatarUrl || null,
        },
        simulationDetailsInfo: {
          simulationDetailtabs,
          additionalSectionTitle,
          additionalSectionInfo,
          additionalSectionUuid,
          callDetailsAuthorOverride: apiData.callDetailsAuthorOverride ?? false,
        },
        screenshareEnabled: apiData.screenshareEnabled ?? false,
      },
      content: {
        conversePrompt: this.buildPrompt(
          prompts,
          SimulationConstants.CONVERSE_PROMPT_TYPE
        ),
        cleanTranscriptPrompt: this.buildPrompt(
          prompts,
          SimulationConstants.CLEAN_TRANSCRIPT_PROMPT_TYPE
        ),
        sttPrompt: this.buildPrompt(
          prompts,
          SimulationConstants.STT_PROMPT_TYPE
        ),
      },
      scoring: {
        overallScorePrompt: this.buildPrompt(
          prompts,
          SimulationConstants.OVERALL_SCORE_PROMPT_TYPE
        ),
        messagingPrompt: this.buildPrompt(
          prompts,
          SimulationConstants.MESSAGING_PROMPT_TYPE
        ),
        methodologyPrompt: this.buildPrompt(
          prompts,
          SimulationConstants.METHODOLOGY_PROMPT_TYPE
        ),
        skillPrompt: this.buildPrompt(
          prompts,
          SimulationConstants.SKILL_PROMPT_TYPE
        ),
        sectionWeights: this.transformCalcTypeToSectionWeights(
          apiData.calcType
        ),
        accuracyFeedback: {
          enabled: apiData.accuracyFeedbackEnabled ?? false,
          documentCount: apiData.accuracyDocumentCount ?? 0,
        },
      },
      keywords: transformedKeywords,
      testing: {},
    };
  }

  private buildPrompt(prompts: ISimulationPrompt[], type: string) {
    let match = prompts.find((p) => p.promptType === type);

    // Special handling for messaging prompt to check both V1 and V2
    if (type === SimulationConstants.MESSAGING_PROMPT_TYPE) {
      // First try to find V2, then fallback to V1
      const v2Match = prompts.find(
        (p) => p.promptType === SimulationConstants.MESSAGING_PROMPT_TYPE_V2
      );
      const v1Match = prompts.find(
        (p) => p.promptType === SimulationConstants.MESSAGING_PROMPT_TYPE
      );

      if (v2Match) {
        match = v2Match;
        type = SimulationConstants.MESSAGING_PROMPT_TYPE_V2;
      } else if (v1Match) {
        match = v1Match;
        type = SimulationConstants.MESSAGING_PROMPT_TYPE;
      }
    }

    return {
      promptType: type,
      promptText: match?.promptText || '',
      weightPercentage: 0,
      uuid: match?.uuid || null,
    };
  }

  normalizeCallDetailsToForm(
    callDetails: ISimulationDetail[]
  ): ISimulationDetailsForm {
    const isDefaultTitle = (title: string) =>
      SimulationDetailsDefaultTitles.includes(title);

    const lastEntries = new Map<string, ISimulationDetail>();

    for (const detail of callDetails) {
      if (isDefaultTitle(detail.tabTitle)) {
        const key = `${detail.tabTitle}-${detail.tabSequence}`;
        lastEntries.set(key, detail);
      }
    }

    let mainTabs = Array.from(lastEntries.values())
      .sort((a, b) => a.tabSequence - b.tabSequence)
      .map(({ tabSequence, tabTitle, tabText, uuid }) => ({
        tabSequence,
        tabTitle,
        tabText: tabText || '',
        uuid: uuid || null,
      }));

    // Fallback to defaults if missing
    if (mainTabs.length === 0) {
      mainTabs = SimulationDetailsDefaultTitles.map((title, index) => ({
        tabSequence: index + 1,
        tabTitle: title,
        tabText: '',
        uuid: null,
      }));
    }

    const additional = callDetails.find(
      (item) => !isDefaultTitle(item.tabTitle)
    );

    return {
      simulationDetailtabs: mainTabs,
      additionalSectionTitle: additional?.tabTitle || '',
      additionalSectionInfo: additional?.tabText || '',
      additionalSectionUuid: additional?.uuid || null,
    };
  }

  transformCalcTypeToSectionWeights(calcTypeStr: string): IScoreWeight[] {
    const sectionOrder = this.isSelfAuthoringActive ? SECTION_ORDER : SECTION_ORDER_DEFAULT;
    try {
      const parsed: CalcType = JSON.parse(calcTypeStr);
      const weights = parsed.weights;

      return sectionOrder.map((section) => {
        let rawValue: number | undefined;

        if (section === (SimulationConstants.MESSAGING_SECTION as SectionType)) {
          rawValue =
            weights[SimulationConstants.MESSAGING_PROMPT_TYPE_V2 as keyof typeof weights] ??
            weights[SimulationConstants.MESSAGING_PROMPT_TYPE as keyof typeof weights];
        } else {
          const key = SECTION_TO_WEIGHT_KEY[section];
          rawValue = weights[key as keyof typeof weights];
        }

        return {
          section,
          value: parseFloat(((rawValue ?? 0) * 100).toFixed(2)),
          // Self-authoring omits disabled sections from calcType, so a present key (even 0) means enabled.
          disabled: this.isSelfAuthoringActive ? rawValue === undefined : !rawValue,
        };
      });
    } catch (e) {
      console.error('Invalid calcType JSON', e);
      return sectionOrder.map((section) => ({ section, value: 0, disabled: true }));
    }
  }

  updateSectionWeightsForMessagingPromptType(
    uuid: string,
    newPromptType: string
  ): void {
    const subject = this.simulations.get(uuid);
    if (!subject) return;

    const currentForm = subject.value;
    const scoringGroup = currentForm.get('scoring') as FormGroup;
    const sectionWeights = scoringGroup?.get('sectionWeights')?.value || [];

    if (sectionWeights.length === 0) {
      console.warn('No section weights available to update');
      return;
    }

    const updatedWeights = sectionWeights.map((weight: IScoreWeight) => {
      if (weight.section === SimulationConstants.MESSAGING_SECTION) {
        // Create a new object with a temporary property to force change detection
        return { ...weight, _tempTrigger: Date.now() };
      }
      return weight;
    });

    this.patchForm(uuid, 'scoring', { sectionWeights: updatedWeights });
  }

  public buildCalcTypeFromSectionWeights(weights: IScoreWeight[]): string {
    const weightMap: Record<string, number> = {};

    for (const weight of weights) {
      if (this.isSelfAuthoringActive && weight.disabled) continue;

      const key = SECTION_TO_WEIGHT_KEY[weight.section];
      weightMap[key] = weight.disabled ? 0 : weight.value / 100;
    }

    const calcType: CalcType = {
      type: 'WEIGHTAVG',
      weights: weightMap as CalcType['weights'],
    };

    return JSON.stringify(calcType);
  }

  extractKeysFromFormArray(formArray: FormArray): string[] {
    if (!formArray) return [];
    return formArray.controls
      .map((control) => {
        const formGroup = control as FormGroup;
        return formGroup.get('key')?.value || '';
      })
      .filter((key) => key && key.trim().length > 0);
  }

  syncFormArrayToControl(
    formArray: FormArray,
    formControl: FormControl<string[]>,
    isUpdatingFromFormArray: { set: (value: boolean) => void }
  ): void {
    if (!formArray) return;

    isUpdatingFromFormArray.set(true);
    const keys = this.extractKeysFromFormArray(formArray);
    formControl.setValue(keys, { emitEvent: false });
    isUpdatingFromFormArray.set(false);
  }

  syncWordItemsToFormArray(
    formArray: FormArray,
    wordItems: WordItem[],
    defaultScope: string = SimulationConstants.KEYWORDS_DEFAULT_SCOPE,
    defaultTypeName: string = SimulationConstants.CALL_WORDS_SECTION,
    isUpdatingFromFormArray: { set: (value: boolean) => void }
  ): void {
    if (!formArray) return;

    isUpdatingFromFormArray.set(true);

    const currentItems = formArray.getRawValue() as IWordDefinition[];

    const existingMapByKey = new Map<string, IWordDefinition>();
    const existingMapByTrimmedKey = new Map<string, IWordDefinition>();
    const uuidToItemMap = new Map<string, IWordDefinition>();
    const matchedUuids = new Set<string>();

    currentItems.forEach((item) => {
      if (item.key && item.key.trim().length > 0) {
        const trimmedKey = item.key.trim();
        existingMapByKey.set(item.key, item);
        if (!existingMapByTrimmedKey.has(trimmedKey)) {
          existingMapByTrimmedKey.set(trimmedKey, item);
        }
      }
      if (item.definitionUuid && item.definitionUuid.trim().length > 0) {
        uuidToItemMap.set(item.definitionUuid, item);
      }
    });

    const existingWordTypeUuid =
      currentItems.find(
        (item) => item.wordTypeUuid && item.wordTypeUuid.trim().length > 0
      )?.wordTypeUuid || '';

    const newItems: IWordDefinition[] = [];
    let globalIndex = 0;

    wordItems.forEach((item) => {
      const trimmedKey = item.value?.trim() || '';
      if (!trimmedKey) return;

      let existingItem: IWordDefinition | undefined;
      if (item.uuid && item.uuid.trim().length > 0) {
        existingItem = currentItems.find(
          (word) =>
            word.definitionUuid &&
            word.definitionUuid.trim() === item.uuid!.trim()
        );
      }

      if (!existingItem) {
        existingItem = existingMapByKey.get(item.value || '');
      }

      if (!existingItem) {
        existingItem = existingMapByTrimmedKey.get(trimmedKey);
      }

      if (existingItem) {
        if (existingItem.definitionUuid) {
          matchedUuids.add(existingItem.definitionUuid);
        }
        newItems.push({
          key: item.value || '',
          value: null,
          scope: existingItem.scope,
          typeName: existingItem.typeName,
          definitionUuid: item.uuid || existingItem.definitionUuid,
          wordTypeUuid: existingItem.wordTypeUuid,
          scopeUuid: existingItem.scopeUuid,
        });
      } else {
        let shouldPreserveUuid = false;
        let uuidToPreserve = '';
        let itemToPreserve: IWordDefinition | null = null;

        if (globalIndex < currentItems.length) {
          const itemAtSameIndex = currentItems[globalIndex];
          const hasUuid =
            itemAtSameIndex.definitionUuid &&
            itemAtSameIndex.definitionUuid.trim().length > 0;
          const uuidNotMatched =
            hasUuid && !matchedUuids.has(itemAtSameIndex.definitionUuid);

          if (uuidNotMatched) {
            shouldPreserveUuid = true;
            uuidToPreserve = itemAtSameIndex.definitionUuid;
            itemToPreserve = itemAtSameIndex;
            matchedUuids.add(itemAtSameIndex.definitionUuid);
          }
        }

        if (shouldPreserveUuid && itemToPreserve) {
          newItems.push({
            key: item.value || '',
            value: null,
            scope: itemToPreserve.scope,
            typeName: itemToPreserve.typeName,
            definitionUuid: item.uuid || uuidToPreserve,
            wordTypeUuid: itemToPreserve.wordTypeUuid || existingWordTypeUuid,
            scopeUuid: itemToPreserve.scopeUuid,
          });
        } else {
          newItems.push({
            key: item.value || '',
            value: null,
            scope: defaultScope,
            typeName: defaultTypeName,
            definitionUuid: item.uuid || '',
            wordTypeUuid: existingWordTypeUuid,
          });
        }
      }

      globalIndex++;
    });

    formArray.clear({ emitEvent: false });
    newItems.forEach((item) => {
      formArray.push(
        this.fb.group({
          key: [item.key],
          value: [item.value],
          scope: [item.scope],
          typeName: [item.typeName],
          definitionUuid: [item.definitionUuid],
          wordTypeUuid: [item.wordTypeUuid],
          scopeUuid: [item.scopeUuid || ''],
        }),
        { emitEvent: false }
      );
    });

    isUpdatingFromFormArray.set(false);
  }

  syncFormArrayToPairedFieldsControl(
    formArray: FormArray,
    formControl: FormControl<PairedFieldItem[]>,
    isUpdatingFromFormArray: { set: (value: boolean) => void }
  ): void {
    if (!formArray) return;

    isUpdatingFromFormArray.set(true);

    const items = formArray.getRawValue() as IWordDefinition[];

    // Convert IWordDefinition[] to PairedFieldItem[]
    const pairedFieldItems: PairedFieldItem[] = items.map((item) => ({
      key: item.key || '',
      value: item.value || '',
      uuid: item.definitionUuid || undefined,
    }));

    formControl.setValue(pairedFieldItems, { emitEvent: false });
    isUpdatingFromFormArray.set(false);
  }

  syncPairedFieldsToFormArray(
    formArray: FormArray,
    pairedFieldItems: PairedFieldItem[],
    defaultScope: string = SimulationConstants.KEYWORDS_DEFAULT_SCOPE,
    defaultTypeName: string = SimulationConstants.REPLACE_WORDS_SECTION,
    isUpdatingFromFormArray: { set: (value: boolean) => void }
  ): void {
    if (!formArray) return;

    isUpdatingFromFormArray.set(true);

    const currentItems = formArray.getRawValue() as IWordDefinition[];

    const existingMapByKey = new Map<string, IWordDefinition>();
    const existingMapByTrimmedKey = new Map<string, IWordDefinition>();
    const existingMapByKeyAndValue = new Map<string, IWordDefinition>();
    const existingMapByValue = new Map<string, IWordDefinition[]>();
    const matchedUuids = new Set<string>();

    currentItems.forEach((item) => {
      if (item.key && item.key.trim().length > 0) {
        const trimmedKey = item.key.trim();
        const trimmedValue = item.value ? item.value.trim() : '';
        existingMapByKey.set(item.key, item);
        if (!existingMapByTrimmedKey.has(trimmedKey)) {
          existingMapByTrimmedKey.set(trimmedKey, item);
        }
        const keyValueKey = `${trimmedKey}|${trimmedValue}`;
        if (!existingMapByKeyAndValue.has(keyValueKey)) {
          existingMapByKeyAndValue.set(keyValueKey, item);
        }
        if (trimmedValue) {
          if (!existingMapByValue.has(trimmedValue)) {
            existingMapByValue.set(trimmedValue, []);
          }
          existingMapByValue.get(trimmedValue)!.push(item);
        }
      }
    });

    const existingWordTypeUuid =
      currentItems.find(
        (item) => item.wordTypeUuid && item.wordTypeUuid.trim().length > 0
      )?.wordTypeUuid || '';

    // Pre-claim every uuid held by a paired-field row so an unsaved duplicate can never inherit another row's definitionUuid
    pairedFieldItems.forEach((item) => {
      if (item.uuid && item.uuid.trim().length > 0) {
        matchedUuids.add(item.uuid.trim());
      }
    });

    const newItems: IWordDefinition[] = [];
    let globalIndex = 0;

    pairedFieldItems.forEach((item) => {
      const trimmedKey = item.key.trim();
      const trimmedValue = item.value ? item.value.trim() : '';
      const itemUuid = item.uuid ? item.uuid.trim() : '';

      // Reuse an existing row only if unclaimed by another row; self-match stays allowed so key edits keep scope/wordType metadata
      const isAvailable = (word?: IWordDefinition): boolean =>
        !!word &&
        (!word.definitionUuid ||
          !matchedUuids.has(word.definitionUuid) ||
          word.definitionUuid.trim() === itemUuid);

      // First try to match by UUID (most accurate for duplicates)
      let existingItem: IWordDefinition | undefined;
      if (itemUuid.length > 0) {
        existingItem = currentItems.find(
          (word) =>
            word.definitionUuid &&
            word.definitionUuid.trim() === itemUuid &&
            word.key &&
            word.key.trim() === trimmedKey
        );
      }

      // If no UUID match, try matching by key and value
      if (!existingItem) {
        const candidate = existingMapByKeyAndValue.get(
          `${trimmedKey}|${trimmedValue}`
        );
        if (isAvailable(candidate)) {
          existingItem = candidate;
        }
      }

      if (!existingItem) {
        const candidate = existingMapByKey.get(item.key);
        if (isAvailable(candidate)) {
          existingItem = candidate;
        }
      }

      if (!existingItem) {
        const candidate = existingMapByTrimmedKey.get(trimmedKey);
        if (isAvailable(candidate)) {
          existingItem = candidate;
        }
      }

      if (!existingItem && trimmedValue) {
        const itemsWithSameValue = existingMapByValue.get(trimmedValue);
        if (itemsWithSameValue && itemsWithSameValue.length > 0) {
          const unmatchedItems = itemsWithSameValue.filter(
            (word) => !!word.definitionUuid && isAvailable(word)
          );
          if (unmatchedItems.length === 1) {
            existingItem = unmatchedItems[0];
          }
        }
      }

        if (existingItem) {
          if (existingItem.definitionUuid) {
            matchedUuids.add(existingItem.definitionUuid);
          }
          newItems.push({
          key: item.key,
          value: item.value || null,
            scope: existingItem.scope,
            typeName: existingItem.typeName,
          definitionUuid: item.uuid || existingItem.definitionUuid,
            wordTypeUuid: existingItem.wordTypeUuid,
            scopeUuid: existingItem.scopeUuid,
          });
        } else {
          let shouldPreserveUuid = false;
          let uuidToPreserve = '';
          let itemToPreserve: IWordDefinition | null = null;

          if (globalIndex < currentItems.length) {
            const itemAtSameIndex = currentItems[globalIndex];
            const hasUuid =
              itemAtSameIndex.definitionUuid &&
              itemAtSameIndex.definitionUuid.trim().length > 0;
            const uuidNotMatched = hasUuid && isAvailable(itemAtSameIndex);

            if (uuidNotMatched) {
              const oldValue = itemAtSameIndex.value
                ? itemAtSameIndex.value.trim()
                : '';
            const newValue = trimmedValue;

              if (
                (oldValue && newValue && oldValue === newValue) ||
                (!oldValue && newValue)
              ) {
                shouldPreserveUuid = true;
                uuidToPreserve = itemAtSameIndex.definitionUuid;
                itemToPreserve = itemAtSameIndex;
                matchedUuids.add(itemAtSameIndex.definitionUuid);
              }
            }
          }

          if (shouldPreserveUuid && itemToPreserve) {
            newItems.push({
            key: item.key,
            value: item.value || null,
              scope: itemToPreserve.scope,
              typeName: itemToPreserve.typeName,
            definitionUuid: item.uuid || uuidToPreserve,
              wordTypeUuid: itemToPreserve.wordTypeUuid || existingWordTypeUuid,
              scopeUuid: itemToPreserve.scopeUuid,
            });
          } else {
            newItems.push({
            key: item.key,
            value: item.value || null,
              scope: defaultScope,
              typeName: defaultTypeName,
            definitionUuid: item.uuid || '',
              wordTypeUuid: existingWordTypeUuid,
            });
          }
        }

        globalIndex++;
    });

    formArray.clear({ emitEvent: false });
    newItems.forEach((item) => {
      formArray.push(
        this.fb.group({
          key: [item.key],
          value: [item.value],
          scope: [item.scope],
          typeName: [item.typeName],
          definitionUuid: [item.definitionUuid],
          wordTypeUuid: [item.wordTypeUuid],
          scopeUuid: [item.scopeUuid || ''],
        }),
        { emitEvent: false }
      );
    });

    isUpdatingFromFormArray.set(false);
  }
}
